<script lang="ts">
  // Portions adapted from AnalyseTool (Apache-2.0), (c) Nikolai Davydov. Modified by apibim.
  //
  // Renders a family's 3D preview with three.js. Unlike the AnalyseTool original, the mesh is not
  // requested from a live Revit session — it is a static artefact built at publish time and stored in
  // object storage, so the same component works identically in the browser storefront and in Revit's
  // WebView. It fetches the gzipped mesh JSON from `meshUrl` (the browser inflates it transparently),
  // caches it in IndexedDB, and draws it.

  import { getCachedMesh, setCachedMesh, type FamilyMeshData } from "./familyCache";
  import { t } from "@/i18n/index.svelte";

  let {
    /** Published family: fetch the static mesh artefact from storage, cached by slug. */
    meshUrl,
    /** Stable per-family key for the cache (the slug). Required with meshUrl. */
    cacheKey,
    /** Publish preview: render an already-extracted mesh directly, no fetch and no cache. */
    mesh = null,
    class: className = "",
  }: {
    meshUrl?: string;
    cacheKey?: string;
    mesh?: FamilyMeshData | null;
    class?: string;
  } = $props();

  let container = $state<HTMLDivElement | null>(null);
  let viewerState = $state<"loading" | "ready" | "empty" | "error">("loading");
  let message = $state("");

  // three.js objects are kept out of Svelte's reactivity (plain let, not $state) — proxying them
  // breaks WebGL.
  let THREE: any = null;
  let OrbitControlsClass: any = null;
  let renderer: any = null;
  let scene: any = null;
  let camera: any = null;
  let controls: any = null;
  let modelGroup: any = null;
  let frame = 0;
  let resizeObserver: ResizeObserver | null = null;
  let disposed = false;

  async function fetchMesh(): Promise<FamilyMeshData | null> {
    // Publish preview: the mesh is already in hand, render it as-is.
    if (mesh) return mesh;
    if (!meshUrl) return null;

    // The mesh URL embeds the version id, so it is also the cache version tag: a republished family
    // gets a new URL and thus a fresh cache entry.
    if (cacheKey) {
      const cached = await getCachedMesh(cacheKey, meshUrl);
      if (cached) return cached;
    }

    const res = await fetch(meshUrl);
    if (!res.ok) throw new Error(`Failed to load the 3D preview (${res.status}).`);
    const fetched = (await res.json()) as FamilyMeshData;

    if (cacheKey) void setCachedMesh(cacheKey, meshUrl, fetched);
    return fetched;
  }

  async function ensureThree() {
    if (THREE) return;
    THREE = await import("three");
    const mod = await import("three/examples/jsm/controls/OrbitControls.js");
    OrbitControlsClass = mod.OrbitControls;
  }

  function setupScene() {
    const el = container!;
    const width = el.clientWidth || 600;
    const height = el.clientHeight || 400;

    scene = new THREE.Scene();
    camera = new THREE.PerspectiveCamera(45, width / height, 0.01, 100000);

    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(window.devicePixelRatio || 1);
    renderer.setSize(width, height);
    el.appendChild(renderer.domElement);

    scene.add(new THREE.HemisphereLight(0xffffff, 0x444444, 1.1));
    const dir = new THREE.DirectionalLight(0xffffff, 1.3);
    dir.position.set(1, 1.5, 1);
    scene.add(dir);

    controls = new OrbitControlsClass(camera, renderer.domElement);
    controls.enableDamping = true;

    resizeObserver = new ResizeObserver(onResize);
    resizeObserver.observe(el);
    animate();
  }

  function disposeModel() {
    if (!modelGroup) return;
    scene?.remove(modelGroup);
    modelGroup.traverse((o: any) => {
      o.geometry?.dispose?.();
      o.material?.dispose?.();
    });
    modelGroup = null;
  }

  function buildModel(data: FamilyMeshData) {
    disposeModel();

    const group = new THREE.Group();
    const geometries: any[] = [];
    for (const part of data.parts ?? []) {
      const geom = new THREE.BufferGeometry();
      geom.setAttribute("position", new THREE.Float32BufferAttribute(part.positions, 3));
      geom.setIndex(part.indices);
      geom.computeVertexNormals();
      const c = part.color ?? [154, 166, 178];
      const r = c[0] ?? 154;
      const g = c[1] ?? 166;
      const b = c[2] ?? 178;
      const transparent = part.opacity < 1;
      const material = new THREE.MeshStandardMaterial({
        color: new THREE.Color(r / 255, g / 255, b / 255),
        metalness: 0.1,
        roughness: 0.75,
        side: THREE.DoubleSide,
        transparent,
        opacity: part.opacity,
        depthWrite: !transparent,
      });
      group.add(new THREE.Mesh(geom, material));
      geometries.push(geom);
    }

    // Recentre on the combined bounding box (all parts move together), then rotate to Revit's Z-up.
    const box = new THREE.Box3().setFromObject(group);
    const center = box.getCenter(new THREE.Vector3());
    geometries.forEach((g) => g.translate(-center.x, -center.y, -center.z));
    group.rotateX(-Math.PI / 2);
    scene.add(group);
    modelGroup = group;

    // Frame the model on its bounding sphere at a comfortable distance.
    const radius = box.getBoundingSphere(new THREE.Sphere()).radius || 1;
    const dist = radius / Math.sin((camera.fov * Math.PI) / 360);
    camera.position.set(dist, dist * 0.85, dist);
    camera.near = Math.max(radius / 1000, 0.001);
    camera.far = dist * 12;
    camera.updateProjectionMatrix();
    controls.target.set(0, 0, 0);
    controls.update();
  }

  async function load() {
    // Traced because this component can only fail in one visible way — the "loading" caption never
    // going away — and every route out of that state is behind an await, so a stall leaves no
    // exception and no clue. Inside Revit these lines reach the plugin log through the DevTools
    // console bridge, which is the only window into the WebView there is.
    console.debug("[viewer] load: start");
    viewerState = "loading";
    let data: FamilyMeshData | null;
    try {
      data = await fetchMesh();
    } catch (e) {
      console.error("Mesh load failed", e);
      viewerState = "error";
      message = String((e as Error)?.message ?? e);
      return;
    }
    if (disposed) {
      console.debug("[viewer] load: abandoned, component already destroyed");
      return;
    }

    if (!data?.available || !data.parts?.length) {
      disposeModel();
      viewerState = "empty";
      message = data?.reason ?? t("viewer.noGeometry");
      console.debug(`[viewer] load: no geometry (${message})`);
      return;
    }

    console.debug(`[viewer] load: ${data.parts.length} part(s), loading three.js`);

    // Inside the try, unlike before. three.js arrives by dynamic import, and an import that fails —
    // a chunk the dev server will not serve, a network hiccup — rejects a promise nobody was
    // catching: the rejection escaped load() entirely, so the component sat on "loading" with no
    // error anywhere. Whatever goes wrong past this point must end up on screen.
    try {
      await ensureThree();
      if (disposed) {
        console.debug("[viewer] load: abandoned after three.js, component already destroyed");
        return;
      }
      if (!renderer) setupScene();

      buildModel(data);
      viewerState = "ready";
      console.debug("[viewer] load: ready");
    } catch (e) {
      console.error("[viewer] load: building the scene failed", e);
      viewerState = "error";
      message = String((e as Error)?.message ?? e);
    }
  }

  function animate() {
    frame = requestAnimationFrame(animate);
    controls?.update();
    if (renderer && scene && camera) renderer.render(scene, camera);
  }

  function onResize() {
    const el = container;
    if (!renderer || !camera || !el) return;
    const w = el.clientWidth;
    const h = el.clientHeight;
    if (!w || !h) return;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h);
  }

  // Runs once on mount, then again whenever meshUrl/mesh change — the explicit reads below make the
  // dependencies unambiguous rather than relying on which lines inside the async load() happen to
  // run before its first await.
  $effect(() => {
    void meshUrl;
    void mesh;
    void load();
  });

  // A second, dependency-free effect purely for teardown: unlike the one above, this must run only
  // once, when the component is destroyed — not every time meshUrl/mesh change, since load() already
  // disposes the old model on rebuild without tearing down the renderer.
  $effect(() => {
    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      controls?.dispose?.();
      disposeModel();
      renderer?.dispose?.();
      if (renderer?.domElement && container?.contains(renderer.domElement))
        container.removeChild(renderer.domElement);
      scene = null;
    };
  });
</script>

<div class="relative w-full h-full bg-panel overflow-hidden {className}">
  <div bind:this={container} class="absolute inset-0"></div>

  {#if viewerState !== "ready"}
    <div
      class="absolute inset-0 flex flex-col items-center justify-center text-ink-muted gap-2 pointer-events-none px-4 text-center"
    >
      {#if viewerState === "loading"}
        <span class="text-sm">{t("viewer.loading")}</span>
      {:else}
        <span class="text-sm">{message}</span>
      {/if}
    </div>
  {/if}
</div>
